import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Card } from "@/components/ui/card";
import { FacebookAd } from "@/hooks/api/use-brand-ads";
import { InfoIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Constants for media types and their properties
const MEDIA_TYPES = {
  VIDEO: "VIDEO",
  IMAGE: "IMAGE",
  CAROUSEL: "CAROUSEL",
  DPA: "DPA",
} as const;

// Human readable names for tooltips
const MEDIA_TYPE_DESCRIPTIONS = {
  dpa: "Dynamic Product Ads",
  dco: "Dynamic Creative Optimization",
};

type MediaType = keyof typeof MEDIA_TYPES;
type MediaTypeKey = Lowercase<MediaType>;

// Color schema for different media types
const MEDIA_COLORS = {
  video: {
    primary: "#818cf8", // indigo-400
    bg: "bg-indigo-500",
    bgDco: "bg-indigo-300",
  },
  image: {
    primary: "#ec4899", // pink-500
    bg: "bg-pink-400",
    bgDco: "bg-pink-300",
  },
  carousel: {
    primary: "#22d3ee", // cyan-400
    bg: "bg-cyan-400",
    bgDco: "bg-cyan-300",
  },
  dpa: {
    primary: "#fb923c", // orange-400
    bg: "bg-orange-400",
    bgDco: "bg-orange-300",
  },
};

/**
 * Determines the actual media type for DCO ads by examining media URLs
 */
const getDcoMediaType = (ad: FacebookAd): MediaTypeKey => {
  const snapshot = ad.latest_snapshot;
  if (!snapshot) return "image"; // Default to image if no snapshot

  // Check if main snapshot has video
  if (snapshot.video_hd_url) {
    return "video";
  }

  // Check if any cards have videos
  const hasVideoInCards = snapshot.cards.some((card) => card.video_hd_url);
  if (hasVideoInCards) {
    return "video";
  }

  // Default to image if no videos found
  return "image";
};

/**
 * Gets the effective media type for an ad, handling DCO specially
 */
const getEffectiveMediaType = (
  ad: FacebookAd
): { type: string; display: string; isDco: boolean } => {
  const format = ad.latest_snapshot?.display_format;
  const displayName = ad.latest_snapshot?.display_format_display || format;

  if (!format) {
    return { type: "IMAGE", display: "Image", isDco: false };
  }

  // For DCO ads, determine if they're using images or videos
  if (format === "DCO") {
    const mediaType = getDcoMediaType(ad);
    return {
      type: mediaType.toUpperCase() as MediaType,
      display: mediaType === "video" ? "Video (DCO)" : "Image (DCO)",
      isDco: true,
    };
  }

  // Format display names for specific types
  if (format === "DPA") {
    return { type: format, display: "DPA", isDco: false };
  }

  return { type: format, display: displayName || format, isDco: false };
};

// Custom tooltip for the chart
interface ChartDataPoint {
  name: string;
  value: number;
  color: string;
  displayName: string;
}

const CustomChartTooltip = ({
  active,
  payload,
  total,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartDataPoint }>;
  total: number;
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-card border border-border rounded-md shadow-md p-2 text-sm">
        <p className="font-medium">{data.displayName}</p>
        <p className="text-muted-foreground">
          {data.value} ({Math.round((data.value / total) * 100)}%)
        </p>
      </div>
    );
  }
  return null;
};

interface MediaMixProps {
  ads: FacebookAd[];
}

interface FormatGroup {
  count: number;
  regular: number;
  dco: number;
  displayName: string;
  key: MediaTypeKey;
  percentage: number;
}

export default function MediaMix({ ads }: MediaMixProps) {
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Filter ads based on toggle state
  const filteredAds = showActiveOnly ? ads.filter((ad) => ad.is_active) : ads;

  // Group ads by effective media type with DCO breakdown
  const formatGroups = filteredAds.reduce((acc, ad) => {
    const { type, isDco } = getEffectiveMediaType(ad);
    const key = type.toLowerCase() as MediaTypeKey;

    if (!acc[type]) {
      acc[type] = {
        count: 0,
        regular: 0,
        dco: 0,
        displayName: key.charAt(0).toUpperCase() + key.slice(1),
        key,
        percentage: 0,
      };
    }

    acc[type].count++;
    if (isDco) {
      acc[type].dco++;
    } else {
      acc[type].regular++;
    }

    return acc;
  }, {} as Record<string, FormatGroup>);

  const total = filteredAds.length;

  // Calculate percentages
  Object.values(formatGroups).forEach((group) => {
    group.percentage = total > 0 ? Math.round((group.count / total) * 100) : 0;
  });

  // Create chart data and sort by value in descending order
  const chartData = Object.entries(formatGroups)
    .map(([format, data]) => {
      const key = data.key;
      return {
        name: format,
        value: data.count,
        color: MEDIA_COLORS[key]?.primary || "#64748b", // Default to slate if color not found
        displayName: data.displayName,
        percentage: data.percentage,
        key,
      };
    })
    .sort((a, b) => b.value - a.value);

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Media Mix</h3>
        <div className="flex items-center space-x-1.5">
          <Switch
            id="active-toggle"
            checked={showActiveOnly}
            onCheckedChange={setShowActiveOnly}
            className="scale-75"
          />
          <Label
            htmlFor="active-toggle"
            className="text-xs text-muted-foreground"
          >
            Active ads only
          </Label>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-center min-h-[150px]">
        <div className="relative flex items-center justify-center">
          <div
            className="absolute top-[70%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center"
            style={{ zIndex: 10 }}
          >
            <div className="text-3xl font-bold">{total}</div>
            <div className="text-sm text-muted-foreground">
              {showActiveOnly ? "active ads" : "ads found"}
            </div>
          </div>
          <div className="relative" style={{ width: 200, height: 100 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="100%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={0}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={<CustomChartTooltip total={total} />}
                  wrapperStyle={{
                    zIndex: 100,
                    outline: "none",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-3 flex-1 ml-8 flex flex-col">
          {chartData.map((item) => {
            const group = formatGroups[item.name];
            const hasDco = group.dco > 0;
            const needsTooltip = item.key === "dpa";
            const hasTooltip =
              needsTooltip &&
              MEDIA_TYPE_DESCRIPTIONS[
                item.key as keyof typeof MEDIA_TYPE_DESCRIPTIONS
              ];

            return (
              <div key={item.name}>
                {hasDco ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <div className="flex items-center gap-3 cursor-pointer group">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="font-medium text-sm">
                                {needsTooltip
                                  ? item.displayName.toUpperCase()
                                  : item.displayName}
                              </span>
                            </TooltipTrigger>
                            {hasTooltip && (
                              <TooltipContent>
                                <p>
                                  {
                                    MEDIA_TYPE_DESCRIPTIONS[
                                      item.key as keyof typeof MEDIA_TYPE_DESCRIPTIONS
                                    ]
                                  }
                                </p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                        <span className="text-muted-foreground text-sm">
                          ({item.percentage}%)
                        </span>
                        <span className="font-semibold text-sm">
                          {item.value}
                        </span>
                        <InfoIcon className="w-3.5 h-3.5 text-muted-foreground/70 opacity-70 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-3">
                      <div className="space-y-2">
                        <div className="text-sm font-medium mb-1">
                          {item.displayName} Breakdown
                        </div>
                        {/* Regular format */}
                        <div className="flex items-center gap-3">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor: MEDIA_COLORS[item.key].primary,
                            }}
                          ></div>
                          <span className="text-sm">Regular</span>
                          <span className="text-muted-foreground text-sm">
                            ({Math.round((group.regular / group.count) * 100)}%)
                          </span>
                          <span className="font-medium text-sm">
                            {group.regular}
                          </span>
                        </div>

                        {/* DCO format */}
                        <div className="flex items-center gap-3">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              backgroundColor:
                                MEDIA_COLORS[item.key].primary + "80",
                            }}
                          ></div>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-sm">DCO</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{MEDIA_TYPE_DESCRIPTIONS.dco}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <span className="text-muted-foreground text-sm">
                            ({Math.round((group.dco / group.count) * 100)}%)
                          </span>
                          <span className="font-medium text-sm">
                            {group.dco}
                          </span>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex items-center gap-3 p-1 -ml-1">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="font-medium text-sm">
                            {needsTooltip
                              ? item.displayName.toUpperCase()
                              : item.displayName}
                          </span>
                        </TooltipTrigger>
                        {hasTooltip && (
                          <TooltipContent>
                            <p>
                              {
                                MEDIA_TYPE_DESCRIPTIONS[
                                  item.key as keyof typeof MEDIA_TYPE_DESCRIPTIONS
                                ]
                              }
                            </p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <span className="text-muted-foreground text-sm">
                      ({item.percentage}%)
                    </span>
                    <span className="font-semibold text-sm">{item.value}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
